import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAnimeReveal } from './motion/useAnimeReveal';

interface DuskHeroProps {
  headline?: string;
  sub?: string;
  primaryCta?: { label: string; to: string };
  secondaryCta?: { label: string; to: string };
}

const DuskHero: React.FC<DuskHeroProps> = ({
  headline = 'Institution-grade passive yield infrastructure',
  sub = 'Secure, non-custodial staking on high-performance validator nodes — built for reliability, scale, and consistent rewards.',
  primaryCta = { label: 'Stake now', to: '/stake' },
  secondaryCta = { label: 'Services', to: '/services' },
}) => {
  const ref = useAnimeReveal<HTMLElement>({ delay: 90, translateY: 22, duration: 800 });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const dragEl = dragRef.current;
    if (!canvas || !dragEl) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    let w = 0;
    let h = 0;
    let t = 0;

    let rotX = 0.38;
    let rotY = 0;
    let velX = 0;
    let velY = 0.14;
    let spinDir = 1;
    let dragging = false;
    let lastPx = 0;
    let lastPy = 0;
    let lastTs = 0;

    type Vec3 = { x: number; y: number; z: number };
    type Edge = [number, number];

    let nodes: Vec3[] = [];
    let edges: Edge[] = [];
    let pulse: number[] = [];

    const buildMesh = () => {
      const nx = 5;
      const ny = 4;
      const nz = 5;
      const spacing = 1;
      nodes = [];
      edges = [];
      pulse = [];

      const idx = (i: number, j: number, k: number) => i + j * nx + k * nx * ny;

      for (let k = 0; k < nz; k++) {
        for (let j = 0; j < ny; j++) {
          for (let i = 0; i < nx; i++) {
            nodes.push({
              x: (i - (nx - 1) / 2) * spacing,
              y: (j - (ny - 1) / 2) * spacing * 0.85,
              z: (k - (nz - 1) / 2) * spacing,
            });
            pulse.push(Math.random());
            if (i + 1 < nx) edges.push([idx(i, j, k), idx(i + 1, j, k)]);
            if (j + 1 < ny) edges.push([idx(i, j, k), idx(i, j + 1, k)]);
            if (k + 1 < nz) edges.push([idx(i, j, k), idx(i, j, k + 1)]);
          }
        }
      }
    };

    const rotate = (p: Vec3, ax: number, ay: number): Vec3 => {
      const cosY = Math.cos(ay);
      const sinY = Math.sin(ay);
      const x1 = p.x * cosY - p.z * sinY;
      const z1 = p.x * sinY + p.z * cosY;
      const cosX = Math.cos(ax);
      const sinX = Math.sin(ax);
      const y2 = p.y * cosX - z1 * sinX;
      const z2 = p.y * sinX + z1 * cosX;
      return { x: x1, y: y2, z: z2 };
    };

    const clampX = (v: number) => Math.max(-1.1, Math.min(1.1, v));

    const pointerPos = (e: PointerEvent) => ({ x: e.clientX, y: e.clientY });

    const onPointerDown = (e: PointerEvent) => {
      dragging = true;
      velX = 0;
      velY = 0;
      const p = pointerPos(e);
      lastPx = p.x;
      lastPy = p.y;
      lastTs = performance.now();
      dragEl.setPointerCapture(e.pointerId);
      dragEl.classList.add('cursor-grabbing');
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) return;
      const p = pointerPos(e);
      const now = performance.now();
      const dt = Math.max(8, now - lastTs);
      const dx = p.x - lastPx;
      const dy = p.y - lastPy;
      const sens = 0.0055;
      rotY += dx * sens;
      rotX = clampX(rotX + dy * sens);
      velY = (dx * sens) / (dt / 16);
      velX = (dy * sens) / (dt / 16);
      lastPx = p.x;
      lastPy = p.y;
      lastTs = now;
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      dragEl.classList.remove('cursor-grabbing');
      try {
        dragEl.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
      // keep a bit of spin; fall back to gentle auto-rotate
      if (Math.abs(velY) >= 0.02) spinDir = Math.sign(velY);
      if (Math.abs(velY) < 0.04) velY = spinDir * 0.09;
      if (Math.abs(velX) < 0.01) velX = 0;
    };

    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = () => {
      t += 0.006;

      if (!dragging) {
        rotY += velY * 0.016;
        rotX = clampX(rotX + velX * 0.016);
        const targetSpin = spinDir * 0.11;
        velY += (targetSpin - velY) * 0.012;
        velX *= 0.96;
      }

      ctx.clearRect(0, 0, w, h);

      const blobs = [
        { x: w * 0.22 + Math.sin(t * 0.55) * 28, y: h * 0.55 + Math.cos(t * 0.4) * 22, r: w * 0.34, c: [208, 0, 104], a: 0.1 },
        { x: w * 0.78 + Math.cos(t * 0.45) * 36, y: h * 0.38 + Math.sin(t * 0.5) * 26, r: w * 0.36, c: [242, 61, 134], a: 0.07 },
        { x: w * 0.62 + Math.sin(t * 0.35) * 20, y: h * 0.78 + Math.cos(t * 0.38) * 18, r: w * 0.26, c: [168, 0, 90], a: 0.055 },
      ];

      for (const b of blobs) {
        const g = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r);
        g.addColorStop(0, `rgba(${b.c[0]},${b.c[1]},${b.c[2]},${b.a})`);
        g.addColorStop(1, `rgba(${b.c[0]},${b.c[1]},${b.c[2]},0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
        ctx.fill();
      }

      const ax = rotX;
      const ay = rotY;
      const scale = Math.min(w, h) * (w < 720 ? 0.11 : 0.13);
      const fov = 7.2;
      const cx = w * (w < 720 ? 0.52 : 0.68);
      const cy = h * 0.48;

      const projected = nodes.map((n) => {
        const r = rotate(n, ax, ay);
        const persp = fov / (fov + r.z);
        return {
          x: cx + r.x * scale * persp,
          y: cy + r.y * scale * persp,
          z: r.z,
          p: persp,
        };
      });

      for (const [a, b] of edges) {
        const pa = projected[a];
        const pb = projected[b];
        const depth = (pa.p + pb.p) / 2;
        const alpha = 0.06 + depth * 0.16;
        ctx.strokeStyle = `rgba(208,0,104,${alpha})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(pa.x, pa.y);
        ctx.lineTo(pb.x, pb.y);
        ctx.stroke();
      }

      for (let i = 0; i < projected.length; i++) {
        const p = projected[i];
        const beat = 0.55 + 0.45 * Math.sin(t * 1.8 + pulse[i] * Math.PI * 2);
        const r = (1.1 + p.p * 1.4) * (0.85 + beat * 0.2);
        const alpha = 0.18 + p.p * 0.35 * beat;
        ctx.fillStyle = `rgba(242,61,134,${alpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    };

    buildMesh();
    resize();
    window.addEventListener('resize', resize);
    dragEl.addEventListener('pointerdown', onPointerDown);
    dragEl.addEventListener('pointermove', onPointerMove);
    dragEl.addEventListener('pointerup', onPointerUp);
    dragEl.addEventListener('pointercancel', onPointerUp);
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      dragEl.removeEventListener('pointerdown', onPointerDown);
      dragEl.removeEventListener('pointermove', onPointerMove);
      dragEl.removeEventListener('pointerup', onPointerUp);
      dragEl.removeEventListener('pointercancel', onPointerUp);
    };
  }, []);

  return (
    <section ref={ref} className="relative overflow-hidden min-h-[min(88vh,760px)] flex items-center">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        aria-hidden
      />
      <div
        ref={dragRef}
        className="absolute inset-y-0 right-0 w-[58%] z-[5] cursor-grab touch-none select-none"
        aria-hidden
      />

      <div className="dusk-container relative z-10 w-full py-20 md:py-24 pointer-events-none">
        <h1
          data-anime
          className="font-display text-[clamp(3rem,9vw,5.75rem)] leading-[0.92] tracking-[0.04em] text-[var(--dusk-ink)] m-0 mb-5"
        >
          DUSKPROOF
        </h1>
        <p
          data-anime
          className="font-display text-[clamp(1.15rem,2.2vw,1.65rem)] font-medium text-[var(--dusk-ink)]/90 max-w-lg m-0 mb-3 leading-snug"
        >
          {headline}
        </p>
        <p
          data-anime
          className="text-[0.95rem] text-[var(--dusk-muted)] max-w-md leading-relaxed m-0 mb-9"
        >
          {sub}
        </p>
        <div data-anime className="flex flex-wrap gap-3 pointer-events-auto">
          <Link to={primaryCta.to} className="dusk-btn-primary">
            {primaryCta.label}
          </Link>
          <Link to={secondaryCta.to} className="dusk-btn-ghost">
            {secondaryCta.label}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default DuskHero;
